import { useEffect, useRef, useState } from 'react';
import { apiClient } from '../packages/api-client';
import config from '../config/botProtection.json';

// The domain-bound CAPTCHA key is public browser configuration, not an AWS credential.
declare global {
    interface Window {
        awsWafCookieDomainList?: string[];
        AwsWafIntegration?: { getToken(): Promise<string> };
        AwsWafCaptcha?: { renderCaptcha(container: HTMLElement, options: {
            apiKey: string; onSuccess(token: string): void; onError(error: unknown): void;
        }): void };
    }
}
const enabled = () => import.meta.env.VITE_BOT_PROTECTION_ENABLED === 'true' ||
    /(^|\.)kaziatlas\.com$/.test(window.location.hostname);
let sdk: Promise<void> | undefined;
function loadSDK(): Promise<void> {
    if (window.AwsWafCaptcha && window.AwsWafIntegration) return Promise.resolve();
    if (!sdk) {
        sdk = new Promise<void>((resolve, reject) => {
            window.awsWafCookieDomainList = ['kaziatlas.com'];
            const script = document.createElement('script');
            script.src = `${config.integrationURL.replace(/\/$/, '')}/jsapi.js`;
            script.async = true;
            const fail = () => { clearTimeout(timer); script.remove(); sdk = undefined; reject(new Error('The security check could not load. Please check your connection and try again.')); };
            const timer = setTimeout(fail, 15000);
            // jsapi.js loads the token integration asynchronously.
            const ready = () => {
                if (window.AwsWafCaptcha && window.AwsWafIntegration) { clearTimeout(timer); resolve(); }
                else if (script.isConnected) setTimeout(ready, 50);
            };
            script.onload = ready;
            script.onerror = fail;
            document.head.appendChild(script);
        });
    }
    return sdk;
}
async function getToken(): Promise<string> {
    await loadSDK();
    if (!window.AwsWafIntegration) throw new Error('The security check is unavailable. Please try again.');
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        return await Promise.race([
            window.AwsWafIntegration.getToken(),
            new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('The security check timed out. Please try again.')), 15000); }),
        ]);
    } finally { clearTimeout(timer); }
}

export function useBotProtection() {
    const [required, setRequired] = useState(false);
    const [solved, setSolved] = useState(false);
    const [problem, setProblem] = useState('');
    const [attempt, setAttempt] = useState(0);
    const container = useRef<HTMLDivElement>(null);
    const captchaToken = useRef('');
    useEffect(() => {
        if (!required) return;
        let active = true;
        setProblem(''); setSolved(false); captchaToken.current = '';
        const target = container.current;
        if (target) target.replaceChildren();
        loadSDK().then(() => {
            if (!active || !target) return;
            if (!window.AwsWafCaptcha) throw new Error('The security check is unavailable. Please try again.');
            window.AwsWafCaptcha.renderCaptcha(target, {
                apiKey: config.apiKey,
                onSuccess: token => { if (active) { captchaToken.current = token; setSolved(true); setProblem(''); } },
                onError: () => { if (active) { captchaToken.current = ''; setSolved(false); setProblem('The security check could not finish. Please try again.'); } },
            });
        }).catch(error => { if (active) setProblem(error.message); });
        return () => { active = false; target?.replaceChildren(); };
    }, [required, attempt]);

    async function post<T = { message: string }>(url: string, data: unknown) {
        if (!enabled()) return apiClient.post<T>(url, data);
        if (required && !captchaToken.current) throw new Error('Complete the security check below, then submit again.');
        let token: string;
        try { token = required ? captchaToken.current : await getToken(); }
        catch (error) { setRequired(true); throw error; }
        try {
            const response = await apiClient.post<T>(url, data, { headers: {
                'X-Aws-Waf-Token': token,
                ...(required ? { 'X-Require-Captcha': '1' } : {}),
            } });
            // A WAF challenge is 202 with an empty body; never treat it as signup success.
            if (!response.data || typeof response.data !== 'object' || !('message' in response.data)) {
                throw new Error('The security check did not complete. Please try again.');
            }
            return response;
        } catch (error) {
            const failure = error as { response?: { status?: number; data?: { code?: string } } };
            const code = failure.response?.data?.code;
            // WAF challenge responses omit CORS headers, so browsers can expose
            // them as network errors. Show a puzzle; never silently replay a POST.
            if (!failure.response || code === 'captcha_required' || code === 'bot_verification_required' || failure.response.status === 405) {
                setRequired(true); setAttempt(value => value + 1);
                throw new Error('Complete the security check below, then submit again.');
            }
            throw error;
        }
    }
    const widget = required ? <section aria-label="Security check" style={{ margin: '1rem 0' }}>
        <p role="status">{solved ? 'Security check complete. You can submit again.' : 'Please complete this security check, then submit again.'}</p>
        <div ref={container} style={{ background: '#fff', color: '#111', padding: 8, borderRadius: 8, overflowX: 'auto' }} />
        {problem && <><p role="alert">{problem}</p><button type="button" className="btn btn-secondary" onClick={() => setAttempt(value => value + 1)}>Retry security check</button></>}
    </section> : null;
    return { post, widget };
}
